import { ErrorMessage, Field } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { DEV_MODE } from "../../constants";
import AddressForm from "../form/AddressForm";
import AudioRecordButton from "../form/AudioRecordButton";
import Input from "../form/Input";

function formatCommValue(comm) {
  if (comm.areaCode && comm.number?.trim()) {
    return `${comm.areaCode}/${comm.number.trim()}`;
  }
  return comm.number?.trim() || comm.normalized?.trim() || "";
}

function formatContactName(contact) {
  return [contact.firstName, contact.lastName].filter(Boolean).join(" ");
}

function CustomerLookupCard({
  icon,
  title,
  fieldPrefix,
  values,
  setFieldValue,
  onSearch,
  isSearching,
  onNumberChange,
  propertyOptions,
  onSelectOption,
  fetchedFields,
  numberLabel,
  nameLabel,
  nameDisabled,
  contacts = [],
  communications = [],
  searchError = null,
  isLoading = false,
  searchDisabled = false,
  searchDisabledTooltip = "",
}) {
  const { t } = useTranslation();
  const hasMultipleOptions = propertyOptions && propertyOptions.length > 1;
  const hasSingleOption = propertyOptions && propertyOptions.length === 1;

  return (
    <div className="card border-0 mb-4" style={{ position: "relative" }}>
      <div className="card-header h6 mb-0 bg-none p-3">
        <i className={`fa ${icon} fa-lg fa-fw text-dark text-opacity-50 me-1`} /> {title}
        {isLoading && <i className="fa fa-spinner fa-spin ms-2 text-muted" />}
      </div>
      <div className="card-body">
        <div className="row mb-3 gx-3">
          <div className="col-12 col-md-3">
            <label className="form-label" htmlFor={`${fieldPrefix}.number`}>
              {numberLabel}
            </label>
            {hasMultipleOptions ? (
              <select
                name={`${fieldPrefix}.number`}
                className="form-select fs-13px h-45px"
                style={{
                  borderColor: values.is_fetched ? "#00acac" : undefined,
                }}
                value={values.number || ""}
                disabled={hasSingleOption}
                onChange={(e) => onSelectOption(e.target.value)}
              >
                {propertyOptions.map((opt, idx) => (
                  <option key={idx} value={opt.value.number}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <div className="input-group">
                <Field
                  name={`${fieldPrefix}.number`}
                  component={Input}
                  inputMode="numeric"
                  disabled={hasSingleOption}
                  className={`form-control fs-13px h-45px ${
                    searchError ? "is-invalid" : values.is_fetched ? "is-valid" : ""
                  }`}
                  {...(onNumberChange ? { onChange: onNumberChange } : {})}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && onSearch && !searchDisabled) {
                      e.preventDefault();
                      onSearch();
                    }
                  }}
                />
                {onSearch && (
                  <span className="input-group-text p-0 border-0" title={searchDisabled ? searchDisabledTooltip : ""}>
                    <button
                      type="button"
                      className={`btn btn-${values.is_fetched ? "success" : "default"} h-100`}
                      disabled={isSearching || searchDisabled}
                      onClick={onSearch}
                      style={searchDisabled ? { pointerEvents: "none" } : undefined}
                    >
                      <i className={`fa ${isSearching ? "fa-spinner fa-spin" : "fa-search"}`} />
                    </button>
                  </span>
                )}
              </div>
            )}
            <ErrorMessage
              name={`${fieldPrefix}.number`}
              render={(msg) => <div className="invalid-feedback">{t(msg)}</div>}
            />
            {searchError && <div className="text-danger small mt-1">{t(searchError)}</div>}
          </div>
          <div className="col-12 col-md-9">
            <label className="form-label" htmlFor={`${fieldPrefix}.name`}>
              {nameLabel}
            </label>
            <div className="input-group">
              <Field
                name={`${fieldPrefix}.name`}
                component={Input}
                className={`form-control fs-13px h-45px ${fetchedFields?.name ? "is-valid" : ""}`}
                disabled={nameDisabled}
              />
              {!nameDisabled && (
                <AudioRecordButton
                  onTranscription={(text) => {
                    const current = values.name || "";
                    setFieldValue(`${fieldPrefix}.name`, current ? `${current} ${text}` : text);
                  }}
                />
              )}
            </div>
            <ErrorMessage
              name={`${fieldPrefix}.name`}
              render={(msg) => <div className="invalid-feedback">{t(msg)}</div>}
            />
          </div>
        </div>
        <AddressForm
          field={`${fieldPrefix}.addresses`}
          isValid={values.is_fetched}
          isDisabled={values.is_fetched}
          fetchedFields={fetchedFields}
          addresses={values.addresses}
          setFieldValue={setFieldValue}
        />
        {contacts.filter((c) => formatContactName(c).length > 0).length > 0 && (
          <div className="row mb-3 gx-3">
            {contacts
              .filter((c) => formatContactName(c).length > 0)
              .map((contact) => (
                <div key={contact.id} className="col-12 col-md-6 mb-2">
                  <label className="form-label">
                    {[contact.position, contact.department].filter(Boolean).join(", ") || t("contacts.contact")}
                  </label>
                  <input
                    type="text"
                    className="form-control fs-13px h-45px"
                    value={formatContactName(contact)}
                    disabled
                  />
                </div>
              ))}
          </div>
        )}
        {communications.filter((c) => formatCommValue(c).length > 0).length > 0 && (
          <div className="row mb-3 gx-3">
            {communications
              .filter((c) => formatCommValue(c).length > 0)
              .map((comm) => (
                <div key={comm.id} className="col-12 col-md-6 mb-2">
                  <label className="form-label">{comm.medium}</label>
                  <input type="text" className="form-control fs-13px h-45px" value={formatCommValue(comm)} disabled />
                </div>
              ))}
          </div>
        )}
      </div>
      {DEV_MODE && (
        <pre>
          {fieldPrefix}: {JSON.stringify(values, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default CustomerLookupCard;
